import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import SystemHelper from 'features/System/systemHelper';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import CustomDateRangePicker from 'general/components/Others/CustomDateRangePicker';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DialogNotificationAddView from '../DialogNotificationAddView';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';

NotificationConfigTabView.propTypes = {
  showFullUI: PropTypes.bool,
  prefKey: PropTypes.string,
};
const defaultColumnIds = [
  'NOTIFICATION_CODE',
  'TITLE',
  'CONTENT',
  'RECEIVER',
  'CREATED_AT',
  'CREATED_BY',
  'LINK',
  'CATEGORY',
  'STATUS',
  'TIME'
];

function NotificationConfigTabView({ showFullUI = false, prefKey }) {
  // MARK: ---- PROPERTIES ----
  const { user } = useSelector((state) => state.auth);
  const adminRole =
    user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
  const [filter, setFilter] = useState(SystemHelper.Filter.pushNotificationList);
  const [pushNotificationList, setPushNotificationList] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setIsLoading(true);
        const params = {
          ..._.pickBy(filter, value => value !== null && value !== undefined && value !== '')
        };

        const response = await ChargeHelper.API.Notification.requestGetNotificationList(params);
        setPushNotificationList(response?.notifications || []);
        setTotal(response?.total || 0);
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setPushNotificationList([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [filter]);

  const fullColumns = useMemo(() => {
    return [
      {
        id: 'NOTIFICATION_CODE',
        name: UIHelper.buildTableColumn('Mã thông báo'),
        selector: (row) => row.notificationCode ?? row.notificationId,
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'TITLE',
        name: UIHelper.buildTableColumn('Tiêu đề'),
        selector: (row) => row?.title,
        minWidth: '320px',
        style: tableStyle.columns.normal,
        wrap: true,
      },
      {
        id: 'CONTENT',
        name: UIHelper.buildTableColumn('Nội dung'),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.message}>
            <span>
              {row?.message}
            </span>
          </KTTooltip>
        ),
        minWidth: '400px',
        style: tableStyle.columns.normal,
        wrap: true,
      },
      {
        id: 'RECEIVER',
        name: UIHelper.buildTableColumn('Người nhận'),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => {
              if (row?.metadata?.role === 'DRIVER') {
                Utils.openInNewTab(`${AppRoute.customerList}/${row?.user?.userId ?? ''}`);
              } else {
                const url = `/${row?.metadata?.role?.toLowerCase()}/${row?.user?.agencyId ?? ''}`;
                Utils.openInNewTab(url);
              }
            }}
          >
            <KTTooltip offset={[0, 0]} text={row?.user?.fullname ? row?.user?.fullname : ChargeHelper.Utils.getNotificationReceiverName(row?.metadata?.role)}>
              <span>
                {row?.user?.fullname ? row?.user?.fullname : ChargeHelper.Utils.getNotificationReceiverName(row?.metadata?.role)}
              </span>
            </KTTooltip>
          </span>
        ),
        width: '200px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CREATED_AT',
        name: UIHelper.buildTableColumn('Ngày tạo'),
        selector: (row) => Utils.formatDateTimeToString(row?.createdAt, 'DD/MM/YYYY - HH:mm'),
        width: '150px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CREATED_BY',
        name: UIHelper.buildTableColumn('Người tạo'),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row?.createdBy?.fullname ?? row?.name}>
              <span
                className="cursor-pointer hover-opacity-60"
              >
                {row?.creator?.fullname}
              </span>
            </KTTooltip>
          );
        },
        minWidth: '200px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'LINK',
        name: UIHelper.buildTableColumn('Liên kết đến'),
        selector: (row) => {
          return (
            <KTTooltip offset={[0, 0]} text={row?.metadata?.link}>
              <span
                className="cursor-pointer hover-opacity-60"
                onClick={() => Utils.openInNewTab(row?.metadata?.link)}
              >
                {row?.metadata?.link}
              </span>
            </KTTooltip>
          );
        },
        minWidth: '250px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CATEGORY',
        name: UIHelper.buildTableColumn('Danh mục'),
        selector: (row) => ChargeHelper.Utils.getNotificationCategoryName(row?.category),
        minWidth: '100px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'STATUS',
        name: UIHelper.buildTableColumn('Trạng thái'),
        selector: (row) => {
          const status = row?.isSent
            ? ChargeConstants.NotificationStatus.SENT
            : ChargeConstants.NotificationStatus.SCHEDULED
          return (
            <KTLabel
              text={ChargeHelper.Utils.getNotificationStatusName(status)}
              isInline={true}
              size={KTLabelSize.medium}
              additionalClassName={`label-light-${ChargeHelper.Utils.getNotificationStatusColor(
                status
              )}`}
            />
          );
        },
        width: '100px',
      },
      {
        id: 'TIME',
        name: UIHelper.buildTableColumn('Ngày gửi'),
        selector: (row) => Utils.formatDateTimeToString(row?.releaseDate, 'DD/MM/YYYY - HH:mm'),
        width: '150px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        width: '130px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          const actions = buildMoreActions(row);
          return UIHelper.buildMoreActionsButton(actions, true);
        },
      }
    ];
  }, [adminRole]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  // MARK: ---- METHODS ----
  const buildMoreActions = (row) => {
    const actions = [];
    if (adminRole) {
      if (!row?.isSent) {
        // chinh sua
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            handleAddNew(row);
          },
        });
      }
      if (row?.public) {
        // an thong bao
        actions.push({
          text: 'Ẩn thông báo',
          iconElement: <i className="fa-regular fa-message-code" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Notification.showDialogConfirmHideNotification(row, () => {
              handleRefresh();
            });
          },
        });
      } else {
        // hien thong bao
        actions.push({
          text: 'Hiện thông báo',
          iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Notification.showDialogConfirmShowNotification(row, () => {
              handleRefresh();
            });
          },
        });
      }
      if (!row?.isSent) {
        // xoa
        actions.push({
          text: 'Xóa',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.Notification.showDialogConfirmDeleteNotification(row, () => {
              handleRefresh();
            });
          },
        });
      }
    }
    return actions;
  };
  const handleRefresh = () => {
    setFilter({ ...SystemHelper.Filter.pushNotificationList });
  };
  const handleAddNew = (row) => {
    AppCustomDialogHelper.show(
      !!row ? 'Sửa thông báo' : 'Thêm thông báo gửi thủ công',
      <DialogNotificationAddView
        item={row}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          handleRefresh();
          AppCustomDialogHelper.hide();
        }}
      />,
      'lg',
      false,
      true
    );
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div>
          <div className="">
            {/* header */}
            <div className="px-4 d-flex align-items-center justify-content-between">
              {/* title */}
              <div className="d-flex flex-column">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Cấu hình thông báo gửi thủ công
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                    {Utils.formatNumber(total)} thông báo
                  </span>
                </span>
              </div>

              {/* actions */}
              {adminRole &&
                UIHelper.buildGreenButton(
                  'Thêm thông báo',
                  handleAddNew,
                  <i className="fa-regular fa-circle-plus" />
                )}
            </div>

            {/* table */}
            <div>
              {/* filter */}
              <div className="row m-0 p-0 px-2">
                {/* tim kiem */}
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Tìm kiếm')}
                  <KTFormSearch
                    name="search"
                    placeholder="Theo mã thông báo, tiêu đề, nội dung..."
                    value={filter?.q}
                    onSubmit={(text) => {
                      SystemHelper.Filter.pushNotificationList = {
                        ...SystemHelper.Filter.pushNotificationList,
                        q: text,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                  />
                </div>
                {/* loc theo trang thai */}
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Trạng thái')}
                  <KTBSDropdown
                    name="status"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(ChargeHelper.Data.notificationStatusOptions, { value: filter?.isSent })
                          ?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={filter?.isSent}
                    dropdownMenuItems={[
                      { name: 'Tất cả', value: '' },
                      ...ChargeHelper.Data.notificationStatusOptions,
                    ]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      SystemHelper.Filter.pushNotificationList = {
                        ...SystemHelper.Filter.pushNotificationList,
                        isSent: value,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                    isDropdownForm={true}
                  />
                </div>
                {/* ngay tao */}
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Ngày tạo')}
                  <CustomDateRangePicker
                    toggleElementBorderRadius={5}
                    initialValue={filter?.dateFilter}
                    onPressSave={(selectedValue, startDate, endDate) => {
                      SystemHelper.Filter.pushNotificationList = {
                        ...SystemHelper.Filter.pushNotificationList,
                        dateFilter: selectedValue,
                        from: startDate,
                        to: endDate,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                  />
                </div>
                {/* danh muc */}
                <div className="col mb-4 px-2">
                  {UIHelper.buildFormLabel('Danh mục')}
                  <KTBSDropdown
                    name="category"
                    toggleElement={
                      <button
                        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                        type="button"
                      >
                        {_.find(
                          ChargeHelper.Data.notificationCategoryOptions,
                          (item) => item.value === SystemHelper.Filter.pushNotificationList?.category
                        )?.name ?? 'Tất cả'}
                      </button>
                    }
                    selectedValue={SystemHelper.Filter.pushNotificationList?.category}
                    dropdownMenuItems={[
                      { name: 'Tất cả', value: '' },
                      ...ChargeHelper.Data.notificationCategoryOptions,
                    ]}
                    dropdownMenuClassName="overflow-auto w-100"
                    dropdownMenuStyle={{ maxHeight: '30vh' }}
                    onChange={(value) => {
                      SystemHelper.Filter.pushNotificationList = {
                        ...SystemHelper.Filter.pushNotificationList,
                        category: value,
                        page: 0,
                      };
                      handleRefresh();
                    }}
                    isDropdownForm={true}
                  />
                </div>
                {/* thiet lap cot */}
                <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                  {/* bo loc */}
                  {/* <ButtonFilterDropdown
                  dropdownContent={
                    <ChargeVehicleListFilterView
                      onCancel={() => {
                        handleRefresh();
                      }}
                      onApply={() => {
                        handleRefresh();
                      }}
                      initialSelectedBrand={selectedVehicleBrand}
                      initialSelectedModel={selectedVehicleModel}
                      onSelectedBrandChange={(value) => {
                        setSelectedVehicleBrand(value);
                      }}
                      onSelectedModelChange={(value) => {
                        setSelectedVehicleModel(value);
                      }}
                    />
                  }
                /> */}
                  <DropdownTableConfigColumn
                    columnIds={columnIds}
                    fullColumns={fullColumns}
                    fullColumnIds={fullColumnsIdSorted}
                    onChangeCheckboxValue={onChangeCheckboxValue}
                    resetDefaultTableColumn={resetDefaultTableColumn}
                    onChangeFullColumnIds={(ids) => {
                      setFullColumnIds(ids);
                      const key = prefKey + '_FULL_COLUMN_IDS';
                      localStorage.setItem(key, ids.join('|'));
                    }}
                    onSelectAll={onSelectAll}
                    onUnSelectAll={onUnSelectAll}
                    saveChanges={saveChanges}
                  />
                </div>
              </div>
              <div className="px-4">
                <KTTable
                  columns={columns}
                  items={pushNotificationList}
                  selectableRows={false}
                  striped={false}
                  compactGrid={false}
                  onClickedRow={(row) => { }}
                  onSelectedRowsChanged={(rows) => { }}
                  onDoubleClickedRow={(row) => { }}
                  noDataComponent={UIHelper.buildEmptyNoData(
                    'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                  )}
                />
              </div>
            </div>

            {/* pagination */}
            <div className="px-4 mt-2">
              <KTPagination
                currentPage={filter?.page + 1}
                rowCount={total}
                rowsPerPage={filter?.limit}
                currentItemInPageCount={pushNotificationList?.length}
                onChangePage={(newPage) => {
                  const iNewPage = parseInt(newPage);
                  SystemHelper.Filter.pushNotificationList = {
                    ...SystemHelper.Filter.pushNotificationList,
                    page: iNewPage - 1,
                  };
                  handleRefresh();
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                  const iNewRowsPerPage = parseInt(newRowsPerPage);
                  SystemHelper.Filter.pushNotificationList = {
                    ...SystemHelper.Filter.pushNotificationList,
                    limit: iNewRowsPerPage,
                    page: 0,
                  };
                  handleRefresh();
                }}
                loading={false}
                color={KTPaginationColor.success}
                appendText="thông báo"
              />
            </div>
          </div>
        </div>,
        false,
        !showFullUI
      )}
    </div>
  );
}

export default NotificationConfigTabView;
